import http, { endpoints } from '../http';
import { mapAITextQuestionnaireFromDto, mapCopywriteTextSectionFromDto, mapInterviewQuestionFromDto, mapTaskCategoryToOrderTaskDto, mapTaskFiltersFromOrderTaskFiltersDto, mapTaskFromCompatDto, mapTaskFromDto, mapTaskFromOrderTaskListItemDto, mapTaskStatusToOrderTaskDtos, mapVirtualStagingStyleFromDto, } from './helpers';
const efktClient = http.createClient(endpoints.efkt);
/**
 * Comma-joins a list param, dropping unmappable categories and deduping statuses. An all-dropped
 * selection yields `undefined` — no filter, i.e. *everything*, the opposite of what the user picked;
 * `isEfktOnlyCategorySelectionEmpty` in `v2/modules/tasks/hooks.tsx` disables the query instead.
 */
function joinOrderTaskParam(values) {
    const present = Array.from(new Set(values === null || values === void 0 ? void 0 : values.filter((value) => !!value)));
    return present.length ? present.join(',') : undefined;
}
/**
 * EFKT-MOCK DOMAIN — Efkt has no task-list endpoint or task-store concept today, by design: the FE
 * Cutover Atlas states Efkt will have "no persistent task store, ever," modelling customer-input steps
 * as procurement items instead. Every method below mirrors datahub's `TasksApi` 1:1 (same fields, same
 * method surface) so Booqly's task components can be written against a stable shape now — per direction,
 * the working assumption is that BE will front the real procurement-based implementation with a
 * compatibility layer that keeps this shape working from Booqly's side.
 *
 * Most paths below are still an **unconfirmed guess** following Efkt's REST conventions (kebab-case,
 * `/api/` prefix, `{items,total}` envelopes) and will 404 until BE ships a real surface. Grep
 * `EFKT-MOCK` across this package for every placeholder like this one.
 *
 * The guessing turned out to be wrong in a specific, instructive way, so read this before adding a
 * method here. The gateway *does* serve tasks — through a `Compat: Tasks` surface that keeps
 * **datahub's** paths and field names verbatim, with no `/api` prefix. Three methods below are
 * pointed at it and verified against stage (2026-09-03): `getTotalTasksForUser` and
 * `getOpenTasksByCaseId`. Their DTO is `ICompatTaskDto`, not `ITaskDto`. So when a guessed
 * `/api/...` route here 404s, check the live spec for a datahub-named twin before assuming BE has
 * not built it:
 *
 *     https://fe-gateway.stage.efkt.net/openapi/v1.json   (tags `Compat: Tasks`, `Compat: Bookings`)
 *
 * `getList`, `getFilters` and `getOrderTasks` call Efkt's own `/api/orders/tasks`,
 * `/api/orders/tasks/filters` and `/api/order/{orderId}/tasks` (AXIT-836, AXIT-840).
 *
 * Also real, and unrelated to the compat layer: `orderVirtualStaging` and
 * `requestImageObjectsRemoval`/`requestRenovation` call confirmed Efkt endpoints
 * (`POST /api/virtual-staging`, `POST /api/retouching`) — see their own comments.
 * "Book photographer" task completion is not handled here at all; see `BookingsApi` instead.
 */
export default class TasksApi {
    /**
     * REAL — `GET /api/orders/tasks`, Efkt's own task list (AXIT-836). Verified on stage 2026-09-08;
     * branch "EFKT test" returns `total: 43`. Unusually for this gateway it declares a full 200
     * schema in `openapi/v1.json` (`SearchResultOfOrderTaskListItem`), so trust the spec here.
     *
     * **Plural: `/api/order/tasks` is a 404.**
     *
     * **Scope comes from the token, not from `branchIds`** — which narrows *within* it, so omitting it
     * returns everything the caller may see. That is the fix for the compat
     * `POST /orders/tasks/search` this replaced, where an empty `branchIds` silently answered
     * `{"data":[],"total":0}` and left the Efkt half of the Tasks screen empty.
     *
     * **A 200 does not mean "no tasks".** The caller is resolved by the JWT's `email` claim (which the
     * token does carry) against `customers.UserRoleAccounts`, and stage provisions that table by hand
     * — a user with no row there gets `{"items":[],"total":0}` for every query. Measured 2026-09-08
     * for `chau.nb@esoft.com`. An unowned branch in `branchIds` answers 403 instead.
     *
     * **Omitting `statuses` is not "every status"** — BE returns each type's outstanding set, which
     * reproduces datahub's `[Open, Partial]` default. Everything means naming all eleven explicitly.
     */
    static async getList({ search, branchIds, category, status, page = 1, limit = 20, }) {
        var _a, _b;
        const response = await efktClient.get('/api/orders/tasks', {
            params: {
                skip: Math.max(page - 1, 0) * limit,
                take: limit,
                branchIds: (branchIds === null || branchIds === void 0 ? void 0 : branchIds.length) ? branchIds.join(',') : undefined,
                types: joinOrderTaskParam(category === null || category === void 0 ? void 0 : category.map(mapTaskCategoryToOrderTaskDto)),
                statuses: joinOrderTaskParam(status === null || status === void 0 ? void 0 : status.flatMap(mapTaskStatusToOrderTaskDtos)),
                searchString: search || undefined,
            },
        });
        return {
            items: ((_a = response.data.items) !== null && _a !== void 0 ? _a : []).map(mapTaskFromOrderTaskListItemDto),
            total: Number((_b = response.data.total) !== null && _b !== void 0 ? _b : 0),
        };
    }
    /**
     * REAL — `GET /users/{userId}/tasks/total`. Verified on stage 2026-09-03.
     *
     * Answers `200 {"total":0}` for **any** well-formed guid, including one that belongs to nobody —
     * so it is not resolving the user. That is not a bug to work around here: `ICompatTaskDto` carries
     * no assignee field at all, so there is nothing for the backend to count a user's tasks by. The
     * Header badge therefore reads 0 from Efkt until BE adds task ownership. Was
     * `GET /api/user/{userId}/tasks/total`, which 404s.
     */
    static async getTotalTasksForUser(userId) {
        const response = await efktClient.get(`/users/${encodeURIComponent(userId)}/tasks/total`);
        return response.data.total;
    }
    /**
     * REAL — `GET /cases/{caseId}/tasks/open`. Verified on stage 2026-09-03 against a case that has
     * one, and it returns the real row. Was `GET /api/case/{caseId}/tasks/open`, which 404s — note
     * both the missing `/api` and `case` -> `cases`.
     */
    static async getOpenTasksByCaseId(caseId) {
        var _a;
        const response = await efktClient.get(`/cases/${encodeURIComponent(caseId)}/tasks/open`);
        return ((_a = response.data) !== null && _a !== void 0 ? _a : []).map(mapTaskFromCompatDto);
    }
    /**
     * REAL — `GET /api/orders/tasks/filters` (AXIT-840). Replaces the compat filters route, which
     * advertised one stale type and only echoed back the `branches` you passed it.
     *
     * `branches` is **the caller's own accounts**, so it is the only safe source of ids for
     * `getList`'s `branchIds` — an unowned branch answers 403 and fails the whole list. A 200 with
     * `branches: []` means no `customers.UserRoleAccounts` row, never "owns no branch".
     *
     * Over-advertises `Partial`, which nothing emits yet, so filtering by it matches nothing.
     */
    static async getFilters() {
        const response = await efktClient.get('/api/orders/tasks/filters');
        return mapTaskFiltersFromOrderTaskFiltersDto(response.data);
    }
    /** EFKT-MOCK — guessed. Mirrors datahub's `TasksApi.createComment`. */
    static async createComment({ taskId, comment, email }) {
        const body = { comment, addedByEmail: email };
        await efktClient.post(`/api/task/${encodeURIComponent(taskId)}/comments`, body);
    }
    /** EFKT-MOCK — guessed. Mirrors datahub's `TasksApi.completeTask`. */
    static async completeTask(taskId) {
        await efktClient.patch(`/api/task/${encodeURIComponent(taskId)}/complete`);
    }
    /** EFKT-MOCK — guessed. Mirrors datahub's `TasksApi.getTaskDetails`. */
    static async getTaskDetails(taskId) {
        const response = await efktClient.get(`/api/task/${encodeURIComponent(taskId)}`);
        return mapTaskFromDto(response.data);
    }
    /** EFKT-MOCK — guessed. Mirrors datahub's `TasksApi.sendEmailForInterview`. */
    static async sendEmailForInterview(taskId, email, name) {
        await efktClient.post(`/api/task/${encodeURIComponent(taskId)}/interview/send`, { email, name });
    }
    /** EFKT-MOCK — guessed. Mirrors datahub's `TasksApi.sendEmailForAITextQuestionnaire`. */
    static async sendEmailForAITextQuestionnaire(taskId, email, name) {
        await efktClient.post(`/api/task/${encodeURIComponent(taskId)}/questionnaire/send`, { email, name });
    }
    /** EFKT-MOCK — guessed. Mirrors datahub's `TasksApi.getAITextEditorLink`. */
    static async getAITextEditorLink(taskId) {
        const response = await efktClient.get(`/api/task/${encodeURIComponent(taskId)}/ai-text-editor-link`);
        return response.data;
    }
    /**
     * EFKT-MOCK — guessed, order-line-anchored to match the real `/api/order-line/{id}/assets`
     * convention already in this package. Mirrors datahub's `TasksApi.getCopyWriteText`.
     */
    static async getCopywriteTextSections(orderLineId) {
        const response = await efktClient.get(`/api/order-line/${encodeURIComponent(orderLineId)}/copywrite-text-sections`);
        return response.data.map(mapCopywriteTextSectionFromDto);
    }
    /** EFKT-MOCK — guessed. Mirrors datahub's `TasksApi.saveCopyWriteText`. */
    static async saveCopywriteTexts({ taskId, sections }) {
        const body = { taskId, sections };
        await efktClient.post(`/api/task/${encodeURIComponent(taskId)}/copywrite-texts`, body);
    }
    /**
     * EFKT-MOCK — guessed. Mirrors datahub's `TasksApi.getVirtualStagingData` (the style-browsing
     * catalogue for the picker step). Distinct from `orderVirtualStaging` below, which is real —
     * the real Efkt endpoint takes freeform instructions, not a picked style id, so this browsing step
     * has no real backing yet even though the create call it feeds into does.
     */
    static async getVirtualStagingStyles() {
        const response = await efktClient.get('/api/virtual-staging/styles');
        return response.data.map(mapVirtualStagingStyleFromDto);
    }
    /**
     * REAL — `POST /api/virtual-staging`
     * (`Efkt/Applications/Api/Sales/Orders/VirtualStagingEndpoints.cs`), confirmed against the backend
     * source, not guessed. Employee-authenticated only per the endpoint's own comment ("Phase 1 is
     * CC-mediated, customer self-service arrives with the customer portal") — verify against stage
     * before wiring this into a customer-facing hook.
     */
    static async orderVirtualStaging(params) {
        const body = params;
        const response = await efktClient.post('/api/virtual-staging', body);
        return response.data.virtualStagingOrderId;
    }
    /**
     * REAL — `POST /api/retouching` (`Efkt/Applications/Api/Sales/Orders/RetouchingEndpoints.cs`).
     * Covers both datahub's `requestImageObjectsRemoval` and `requestRenovation` — Efkt doesn't
     * distinguish the two task types, both are just a retouching order with a marked-up copy of the
     * original. Same employee-only caveat as `orderVirtualStaging`.
     */
    static async requestRetouching(params) {
        const body = params;
        const response = await efktClient.post('/api/retouching', body);
        return response.data.retouchingOrderId;
    }
    /** EFKT-MOCK — guessed. Mirrors datahub's `TasksApi.createBeforeAfterSlider`. */
    static async createBeforeAfterSlider(taskId, assetIds) {
        const body = { assetIds };
        await efktClient.post(`/api/task/${encodeURIComponent(taskId)}/before-after`, body);
    }
    /** EFKT-MOCK — guessed. Mirrors datahub's `TasksApi.completeSoMeGif`. */
    static async completeSoMeGif(taskId, beforeMaterialId, afterMaterialId) {
        const body = { beforeMaterialId, afterMaterialId };
        await efktClient.post(`/api/task/${encodeURIComponent(taskId)}/some-gif`, body);
    }
    /** EFKT-MOCK — guessed. Mirrors datahub's `TasksApi.getSocialMediaTypes`. */
    static async getSocialMediaTypes() {
        const response = await efktClient.get('/api/tasks/social-media-types');
        return response.data;
    }
    /**
     * Not an EFKT-MOCK network call — datahub's own `TasksApi.getSocialMediaSlideshowTypes` already
     * hardcodes this ("TODO: get from API. Mocked for now"), so this mirrors that mock rather than
     * inventing a guessed endpoint for something the source domain never called either.
     */
    static async getSocialMediaSlideshowTypes() {
        return ['Style1', 'Style2'];
    }
    /** EFKT-MOCK — guessed. Mirrors datahub's `TasksApi.completeSocialMediaTask`. */
    static async completeSocialMediaTask(taskId, type) {
        await efktClient.post(`/api/task/${encodeURIComponent(taskId)}/social-media`, { type });
    }
    /** EFKT-MOCK — guessed. Mirrors datahub's `TasksApi.completeImageSelection`. */
    static async completeImageSelection(taskId, materialIds) {
        const body = { materialIds };
        await efktClient.post(`/api/task/${encodeURIComponent(taskId)}/image-selection`, body);
    }
    /** EFKT-MOCK — guessed. Mirrors datahub's `TasksApi.completeWhiteboxImageSelection`. */
    static async completeWhiteboxImageSelection(taskId, materials) {
        const body = { materials };
        await efktClient.post(`/api/task/${encodeURIComponent(taskId)}/whitebox-image-selection`, body);
    }
    /**
     * REAL — `GET /api/order/{orderId}/tasks` (AXIT-840). `getList`'s row shape, but **a bare array**,
     * reproducing datahub's `GET /orders/{id}/tasks`. **Singular** `order`; the plural is the list.
     *
     * **Outstanding only, with no parameter to widen it**: a booking is here at `Incomplete`/`Created`
     * and gone from `Booked` onwards, so a scheduled visit is simply absent — `useEfktOrderTasks`
     * covers that hole with `BookingsApi.getOrderBookings`. Sorted `createdAt` desc, then `id`.
     *
     * With no `customers.UserRoleAccounts` row the caller's branch set is empty, so **every** order
     * answers 403 — not just a foreign one.
     */
    static async getOrderTasks(orderId) {
        var _a;
        const response = await efktClient.get(`/api/order/${encodeURIComponent(orderId)}/tasks`);
        return ((_a = response.data) !== null && _a !== void 0 ? _a : []).map(mapTaskFromOrderTaskListItemDto);
    }
    /** EFKT-MOCK — guessed. Mirrors datahub's `TasksApi.getInterviewQuestions`. */
    static async getInterviewQuestions(taskId) {
        const response = await efktClient.get(`/api/task/${encodeURIComponent(taskId)}/interview/questions`);
        return response.data.map(mapInterviewQuestionFromDto);
    }
    /** EFKT-MOCK — guessed. Mirrors datahub's `TasksApi.saveInterviewQuestions`. */
    static async saveInterviewAnswers(taskId, answers) {
        const body = { answers };
        await efktClient.post(`/api/task/${encodeURIComponent(taskId)}/interview/answers`, body);
    }
    /** EFKT-MOCK — guessed. Mirrors datahub's `TasksApi.getAITextQuestionnaire`. */
    static async getAITextQuestionnaire(taskId) {
        const response = await efktClient.get(`/api/task/${encodeURIComponent(taskId)}/questionnaire`);
        return mapAITextQuestionnaireFromDto(response.data);
    }
    /** EFKT-MOCK — guessed. Mirrors datahub's `TasksApi.saveAITextQuestionnaire`. */
    static async saveAITextQuestionnaire(taskId, answers) {
        await efktClient.post(`/api/task/${encodeURIComponent(taskId)}/questionnaire`, { answers });
    }
    /** EFKT-MOCK — guessed. Mirrors datahub's `TasksApi.submitUploads`. */
    static async submitTaskUploads(taskId, uploads) {
        const body = { uploads };
        await efktClient.post(`/api/task/${encodeURIComponent(taskId)}/uploads`, body);
    }
    /** EFKT-MOCK — guessed. Mirrors datahub's `TasksApi.getUploads`. */
    static async getTaskUploads(taskId) {
        const response = await efktClient.get(`/api/task/${encodeURIComponent(taskId)}/uploads`);
        return response.data;
    }
}
